// @flow

import { useCallback, useEffect, useMemo, useState } from 'react';

import useHasPermission from '../../../hooks/useHasPermission';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import {
  useDeleteTourBlockout,
  useSaveTourBlockout,
} from '../../../hooks/data-fetching/useTourBlockouts';
import appMessages from '../../App/messages';
import messages from '../messages';
import {
  findSkippedTimeField,
  readTourBlockoutError,
  toFormValues,
  toPayload,
} from '../utils';
import { buildFields } from './fields';
import type { TourBlockout, TourBlockoutFormValues } from '../types';

export type TourBlockoutModalProps = {
  open: boolean,
  tourBlockout: ?TourBlockout,
  timezone: ?string,
  organizationId: string,
  propertyId: string,
  intl: Object,
  handleClose: () => void,
  promptToaster: (payload: Object) => void,
};

const useTourBlockoutModal = ({
  open,
  tourBlockout,
  timezone,
  organizationId,
  propertyId,
  intl,
  handleClose,
  promptToaster,
}: TourBlockoutModalProps) => {
  const isEdit = Boolean(tourBlockout);
  const defaultValues = useMemo(
    () => toFormValues(tourBlockout, timezone),
    [tourBlockout, timezone],
  );
  const ReactHookFormProps = useReactHookFormProps({
    defaultValues,
    mode: 'onSubmit',
  });
  const { handleSubmit, reset, setError, clearErrors } = ReactHookFormProps;
  // A refusal that names no field, such as the property having no time zone.
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setFormError(null);
    }
  }, [open, defaultValues, reset]);

  const save = useSaveTourBlockout({ organizationId, propertyId });
  const isSaving = save.isLoading;

  const onSubmit = useCallback(
    (values: TourBlockoutFormValues) => {
      if (isSaving) return;
      setFormError(null);
      const skippedField = findSkippedTimeField(values, timezone, tourBlockout);
      if (skippedField) {
        setError(skippedField, {
          type: 'conversion',
          message: intl.formatMessage(messages.skippedTime),
        });
        return;
      }
      save.mutate(
        {
          id: tourBlockout?.id,
          payload: toPayload(values, timezone, tourBlockout),
        },
        {
          onSuccess: () => {
            promptToaster({
              title: intl.formatMessage(appMessages.success),
              message: intl.formatMessage(
                isEdit ? messages.updated : messages.created,
              ),
            });
            handleClose();
          },
          onError: (error) => {
            const { message, field } = readTourBlockoutError(
              error,
              intl.formatMessage(messages.saveError),
            );
            if (field) {
              setError(field, { type: 'server', message });
            } else {
              setFormError(message);
            }
          },
        },
      );
    },
    [
      isSaving,
      save,
      tourBlockout,
      timezone,
      promptToaster,
      intl,
      isEdit,
      handleClose,
      setError,
    ],
  );

  // The backend's last answers are cleared before the form is submitted: a
  // field still marked with one would otherwise stop the form submitting at
  // all, even after the user fixed a different field that caused it.
  const handleSave = useCallback(
    (event?: mixed) => {
      clearErrors();
      return handleSubmit(onSubmit)(event);
    },
    [clearErrors, handleSubmit, onSubmit],
  );

  // Delete, as in the Edit Activity modal: a footer control on an existing
  // blockout, behind a confirmation. Hidden, not disabled, without the
  // permission that create and edit need.
  const canManage = useHasPermission('property-activity-manage');
  const canDelete = isEdit && canManage;
  const deleteBlockout = useDeleteTourBlockout({ organizationId, propertyId });
  const isDeleting = deleteBlockout.isLoading;
  const isBusy = isSaving || isDeleting;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const openConfirm = useCallback(() => {
    if (!isBusy) setConfirmOpen(true);
  }, [isBusy]);
  // Left open while the delete runs, so its answer has somewhere to land.
  const closeConfirm = useCallback(() => {
    if (!isDeleting) setConfirmOpen(false);
  }, [isDeleting]);

  useEffect(() => {
    if (!open) setConfirmOpen(false);
  }, [open]);

  // The deleted blockout leaves the list when its query is invalidated
  // (useDeleteTourBlockout). These callbacks run only while this form is still
  // mounted, so a delete that finishes after it closed touches nothing else.
  const handleConfirmDelete = useCallback(() => {
    if (!tourBlockout || isDeleting) return;
    deleteBlockout.mutate(tourBlockout.id, {
      onSuccess: () => {
        promptToaster({
          title: intl.formatMessage(appMessages.success),
          message: intl.formatMessage(messages.deleted),
        });
        setConfirmOpen(false);
        handleClose();
      },
      onError: (error) => {
        promptToaster({
          type: 'error',
          title: intl.formatMessage(appMessages.error),
          message: readTourBlockoutError(
            error,
            intl.formatMessage(messages.deleteError),
          ).message,
        });
      },
    });
  }, [
    tourBlockout,
    isDeleting,
    deleteBlockout,
    promptToaster,
    intl,
    handleClose,
  ]);

  const handleCancel = useCallback(() => {
    if (!isBusy) handleClose();
  }, [isBusy, handleClose]);

  const fields = useMemo(() => buildFields(intl), [intl]);

  const modalProps = useMemo(
    () => ({
      title: intl.formatMessage(
        isEdit ? messages.editTitle : messages.addTitle,
      ),
      ModalProps: { maxWidth: 'sm', fullWidth: true },
      actionsProps: [
        {
          children: intl.formatMessage(appMessages.cancel),
          onClick: handleCancel,
          disabled: isBusy,
        },
        {
          children: intl.formatMessage(appMessages.save),
          submitButton: true,
          isSubmitting: isSaving,
          disabled: isBusy,
          onClick: handleSave,
        },
      ],
      // The library Modal shows its delete control only when given this.
      handleDelete: canDelete ? openConfirm : undefined,
      ConfirmProps: {
        title: intl.formatMessage(messages.deleteConfirmation),
        open: confirmOpen,
        onClose: closeConfirm,
        actionsProps: [
          {
            children: intl.formatMessage(appMessages.no),
            onClick: closeConfirm,
            disabled: isDeleting,
          },
          {
            children: intl.formatMessage(appMessages.yes),
            submitButton: true,
            isSubmitting: isDeleting,
            disabled: isDeleting,
            onClick: handleConfirmDelete,
          },
        ],
      },
    }),
    [
      intl,
      isEdit,
      handleCancel,
      isBusy,
      isSaving,
      isDeleting,
      handleSave,
      canDelete,
      openConfirm,
      confirmOpen,
      closeConfirm,
      handleConfirmDelete,
    ],
  );

  const timezoneNote = timezone
    ? intl.formatMessage(messages.propertyTimezone, { timezone })
    : intl.formatMessage(messages.noPropertyTimezone);

  return {
    ReactHookFormProps,
    fields,
    modalProps,
    handleCancel,
    formError,
    timezoneNote,
  };
};

export default useTourBlockoutModal;
